import { useCallback, useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { AFTELLEN, t } from '../content/content'
import { useKiosk } from '../state/store'

/** Seconden zonder aanraking voordat de vraag verschijnt. */
const STIL = 45
/**
 * In Connectiviteit langer: Warehouse Cockpit staat in een iframe van een
 * ander domein, en aanrakingen dáárin bereiken dit venster niet. Een bezoeker
 * die drie minuten in de cockpit klikt lijkt voor ons weg; de vraag komt dan
 * alsnog, maar pas na een termijn waarin hij er in de praktijk echt is.
 */
const STIL_IFRAME = 180
/** Seconden die de vraag open blijft staan voordat de kiosk terugkeert. */
const AFTEL = 15

/**
 * Het aftellen terug naar het hoofdscherm (feedback 5 oktober, PLAN.md §3).
 *
 * "Herkennen of iemand nog kijkt" doen we met de enige sensor die een kiosk
 * zonder camera heeft: de aanraking. Na 45 seconden stilte verschijnt een
 * vraag met een aflopende ring; wie aanraakt gaat verder, wie wegblijft
 * krijgt na 15 seconden de harde sessie-reset — hoofdscherm, Nederlands,
 * alles gewist. Op het hoofdscherm zelf wordt de vraag niet gesteld: daar is
 * niets te verliezen, dus daar valt de taal stil terug op Nederlands.
 *
 * Twee regels uit het plan die hier als code staan: een lopende video houdt
 * de teller tegen, en hij start opnieuw zodra de film is afgelopen of
 * gesloten. En een aanraking in de beheerhoek telt niet mee — die hoek
 * stopt de propagatie zelf.
 */
export function Inactiviteit() {
  const scherm = useKiosk((s) => s.scherm)
  const taal = useKiosk((s) => s.taal)
  const videoSpeelt = useKiosk((s) => s.videoSpeelt)
  const reset = useKiosk((s) => s.reset)

  const [vraag, setVraag] = useState(false)
  const [rest, setRest] = useState(AFTEL)
  const timer = useRef<number | null>(null)
  const tikker = useRef<number | null>(null)

  const stopTikker = useCallback(() => {
    if (tikker.current !== null) { window.clearInterval(tikker.current); tikker.current = null }
  }, [])

  const stel = useCallback(() => {
    if (timer.current !== null) window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => {
      timer.current = null
      // Op het hoofdscherm valt alleen de taal terug; niemand hoeft daar
      // bevestigd te worden.
      if (useKiosk.getState().scherm === 'hoofdscherm') { reset(); return }
      setRest(AFTEL)
      setVraag(true)
    }, (useKiosk.getState().scherm === 'connectiviteit' ? STIL_IFRAME : STIL) * 1000)
  }, [reset])

  // Elke aanraking of toets herstart de teller en haalt de vraag weg.
  useEffect(() => {
    const activiteit = () => { setVraag(false); if (!useKiosk.getState().videoSpeelt) stel() }
    // Een bewegende muis is ook iemand die kijkt: bij een demo op een laptop
    // praat de presentator en wijst hij met de muis zonder te klikken (21
    // oktober). Alleen de muis — een vinger beweegt nooit zonder pointerdown —
    // en hooguit eens per seconde, want pointermove komt per beeld binnen.
    let laatst = -Infinity
    const beweging = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse' || e.timeStamp - laatst < 1000) return
      laatst = e.timeStamp
      activiteit()
    }
    window.addEventListener('pointerdown', activiteit, { passive: true })
    window.addEventListener('pointermove', beweging, { passive: true })
    window.addEventListener('keydown', activiteit)
    stel()
    return () => {
      window.removeEventListener('pointerdown', activiteit)
      window.removeEventListener('pointermove', beweging)
      window.removeEventListener('keydown', activiteit)
      if (timer.current !== null) window.clearTimeout(timer.current)
    }
  }, [stel])

  // Een spelende film zet de teller stil; het einde ervan start hem opnieuw.
  useEffect(() => {
    if (videoSpeelt) {
      if (timer.current !== null) { window.clearTimeout(timer.current); timer.current = null }
      setVraag(false)
    } else {
      stel()
    }
  }, [videoSpeelt, stel])

  // Een schermwissel is activiteit, ook als hij van de code komt.
  useEffect(() => { setVraag(false); stel() }, [scherm, stel])

  // Het aftellen zelf.
  useEffect(() => {
    if (!vraag) { stopTikker(); return }
    tikker.current = window.setInterval(() => {
      setRest((r) => {
        if (r <= 1) { stopTikker(); setVraag(false); reset(); return AFTEL }
        return r - 1
      })
    }, 1000)
    return stopTikker
  }, [vraag, reset, stopTikker])

  const straal = 54
  const omtrek = 2 * Math.PI * straal

  return (
    <AnimatePresence>
      {vraag && (
        <motion.div
          className="kiosk-aftel"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          role="dialog"
          aria-live="polite"
        >
          <motion.div
            className="kiosk-aftel-paneel"
            initial={{ y: 30, scale: 0.96 }}
            animate={{ y: 0, scale: 1 }}
            exit={{ y: 20, scale: 0.97 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="kiosk-aftel-ring">
              <svg viewBox="0 0 120 120" aria-hidden="true">
                <circle cx="60" cy="60" r={straal} className="kiosk-aftel-spoor" />
                <circle
                  cx="60" cy="60" r={straal}
                  className="kiosk-aftel-loop"
                  style={{
                    strokeDasharray: omtrek,
                    strokeDashoffset: omtrek * (1 - rest / AFTEL),
                  }}
                />
              </svg>
              <span>{rest}</span>
            </div>
            <h2 className="kiosk-aftel-kop">{t(AFTELLEN.kop, taal)}</h2>
            <p className="kiosk-aftel-uitleg">{t(AFTELLEN.uitleg, taal)}</p>
            <button className="kiosk-3d kiosk-3d-vol" onClick={() => setVraag(false)}>
              {t(AFTELLEN.knop, taal)}
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
